import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { DesktopDateRangePicker } from '@mui/x-date-pickers-pro/DesktopDateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import type { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import type { DateRange as MuiDateRange } from '@mui/x-date-pickers-pro/models';
import { addMonths, format, getMonth, getYear, setMonth, setYear } from 'date-fns';
import { useEffect, useState } from 'react';

export type ShortcutDefinition = {
  label: string;
  value: [Date, Date] | string;
};

type Props = {
  /** Controlled committed value [start, end]. */
  value: [Date | null, Date | null];
  /** Preset definitions (only entries with a [start,end] array become shortcuts). Omit for no sidebar. */
  shortcuts?: ShortcutDefinition[];
  /** Fired when the range is committed (Update button, or on selection when applyOnSelect). */
  onAccept: (value: [Date, Date]) => void;
  /** Optional max selectable date (used by the contextualization comparison picker). */
  maxDate?: Date | null;
  /**
   * Bare range selector: no toolbar/helper text and no Update button; the range
   * applies as soon as both dates are picked. Used by the contextualization
   * comparison picker.
   */
  applyOnSelect?: boolean;
};

const fmt = (d?: Date | null) => (d ? format(d, 'dd/MM/yyyy') : '');

// Compare two dates by calendar day (ignore time-of-day) so reverting to the
// same day isn't treated as a change.
const sameDay = (a?: Date | null, b?: Date | null) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// 2-letter weekday headers (Su, Mo, Tu …) like the previous picker.
export const dayOfWeekFormatter = (date: Date) => format(date, 'EEEEEE');

// Modern, app-styled range header — shows the in-progress range as plain navy
// text. The current range is passed explicitly via slotProps.toolbar.rangeValue
// (MUI X v8 does not forward `value` to custom toolbar props).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function RangeToolbar(props: any) {
  const value = props?.rangeValue as MuiDateRange<Date> | undefined;
  const start = value?.[0];
  const end = value?.[1];
  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <Typography sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.05rem' }}>
        {start || end ? `${fmt(start)} - ${fmt(end)}` : 'Select date range'}
      </Typography>
    </Box>
  );
}

const MONTH_INDEXES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// Custom range-calendar header. The default MUI header only lets you step one
// month at a time, so reaching another year means many clicks. This restores
// the previous (rsuite) behaviour: the month and the year are clickable and
// open a picker list, plus there are year-jump arrows. Navigation mirrors MUI's
// default range header (`onMonthChange` shifts the whole 2-month range; the
// prev arrows show on the left calendar, the next arrows on the right one).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function RangeCalendarHeader(props: any) {
  const { currentMonth, month, monthIndex, calendars = 1, onMonthChange, minDate, maxDate } = props;
  const [monthAnchor, setMonthAnchor] = useState<null | HTMLElement>(null);
  const [yearAnchor, setYearAnchor] = useState<null | HTMLElement>(null);

  const isLeft = monthIndex === 0;
  const isRight = monthIndex === calendars - 1;

  // Shift the whole visible range (same as the default arrows).
  const shift = (months: number) => onMonthChange(addMonths(currentMonth, months));
  // Jump THIS calendar to a month/year; subtract its index so the calendar the
  // user clicked ends up showing the chosen month/year.
  const jumpTo = (next: Date) => onMonthChange(addMonths(next, -monthIndex));

  const displayYear = getYear(month);
  const nowYear = new Date().getFullYear();
  let maxYear = nowYear + 5;
  if (maxDate && getYear(maxDate) < maxYear) maxYear = getYear(maxDate);
  let minYear = 2000;
  if (minDate && getYear(minDate) > 1900 && getYear(minDate) < minYear) minYear = getYear(minDate);
  minYear = Math.min(minYear, displayYear);
  maxYear = Math.max(maxYear, displayYear);
  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  const labelSx = {
    minWidth: 0,
    px: 0.75,
    py: 0.25,
    color: 'primary.main',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'none' as const,
    lineHeight: 1.2,
    '&:hover': { backgroundColor: 'action.hover' },
  };
  const arrowSx = { p: '4px', color: 'primary.main' };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '10px',
        pt: '10px',
        pb: '2px',
        minHeight: 36,
      }}
    >
      <Box sx={{ display: 'flex', width: 58, justifyContent: 'flex-start' }}>
        {isLeft && (
          <>
            <IconButton size="small" aria-label="Previous year" onClick={() => shift(-12)} sx={arrowSx}>
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="Previous month" onClick={() => shift(-1)} sx={arrowSx}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Button size="small" disableRipple onClick={(e) => setMonthAnchor(e.currentTarget)} sx={labelSx}>
          {format(month, 'MMMM')}
        </Button>
        <Button size="small" disableRipple onClick={(e) => setYearAnchor(e.currentTarget)} sx={labelSx}>
          {format(month, 'yyyy')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', width: 58, justifyContent: 'flex-end' }}>
        {isRight && (
          <>
            <IconButton size="small" aria-label="Next month" onClick={() => shift(1)} sx={arrowSx}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="Next year" onClick={() => shift(12)} sx={arrowSx}>
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Menu
        anchorEl={monthAnchor}
        open={Boolean(monthAnchor)}
        onClose={() => setMonthAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {MONTH_INDEXES.map((m) => (
          <MenuItem
            key={m}
            dense
            selected={m === getMonth(month)}
            onClick={() => {
              jumpTo(setMonth(month, m));
              setMonthAnchor(null);
            }}
          >
            {format(setMonth(month, m), 'MMMM')}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={yearAnchor}
        open={Boolean(yearAnchor)}
        onClose={() => setYearAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {years.map((y) => (
          <MenuItem
            key={y}
            dense
            selected={y === displayYear}
            onClick={() => {
              jumpTo(setYear(month, y));
              setYearAnchor(null);
            }}
          >
            {y}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// Shared popover styling. Range/selected colours use MUI defaults (theme-based);
// we theme the "Update" button and make the whole popover compact so it doesn't
// dominate the screen (matching the previous, tighter design).
export const dateRangeDesktopPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.18)',
  // Compact calendar cells.
  '& .MuiDateRangePickerDay-root': { height: 32 },
  '& .MuiPickersDay-root, & .MuiDateRangePickerDay-day': {
    width: 30,
    height: 30,
    fontSize: '0.78rem',
  },
  '& .MuiDayCalendar-weekDayLabel': {
    width: 30,
    height: 26,
    margin: 0,
    fontSize: '0.72rem',
  },
  '& .MuiDayCalendar-slideTransition': { minHeight: 196 },
  // Tighter month header + nav.
  '& .MuiPickersCalendarHeader-root': {
    paddingLeft: '8px',
    paddingRight: '8px',
    marginTop: '4px',
    marginBottom: '2px',
    minHeight: 'auto',
  },
  '& .MuiPickersCalendarHeader-label': { fontSize: '0.95rem', fontWeight: 600 },
  '& .MuiPickersArrowSwitcher-button': { padding: '4px' },
  // Compact shortcut chips.
  '& .MuiPickersLayout-shortcuts': { padding: '8px', gap: '2px' },
  '& .MuiPickersLayout-shortcuts .MuiChip-root': { fontSize: '0.8rem', height: 28 },
  // Themed, compact "Update" button.
  '& .MuiDialogActions-root': { padding: '4px 12px 12px' },
  '& .MuiDialogActions-root .MuiButton-root': {
    backgroundColor: 'primary.main',
    color: '#fff',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    '&:hover': { backgroundColor: 'primary.dark' },
  },
};

// Header field styled like the global currency dropdown: a white, rounded pill
// with a primary border (the shared look across all global filters).
// MUI X v8's SingleInputDateRangeField renders MuiPickersOutlinedInput classes
// (not the standard MuiOutlinedInput), so target both for safety.
export const dateRangeFieldSx = {
  maxWidth: 250,
  '& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
    borderRadius: '40px',
    backgroundColor: 'tertiary.main',
    height: 35,
    minWidth: 215,
  },
  '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
  '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    { borderColor: 'primary.main' },
  '& .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root, & input': {
    fontWeight: 600,
    color: 'primary.main',
    fontSize: '0.78rem',
  },
};

/**
 * App-wide date-range picker (replaces rsuite). Two-month calendar, a left
 * shortcuts sidebar, a single rounded pill input, slate range bars with navy
 * start/end, and an "Update" button. The global filter is applied ONLY when
 * "Update" is clicked — selecting dates or a preset just previews.
 */
export default function GlobalDateRangePicker({
  value,
  shortcuts = [],
  onAccept,
  maxDate,
  applyOnSelect = false,
}: Props) {
  const [localValue, setLocalValue] = useState<MuiDateRange<Date>>([value[0], value[1]]);
  const [open, setOpen] = useState(false);

  // Keep the in-progress selection in sync with the committed value from outside
  // (initial default + loading a saved filter).
  useEffect(() => {
    setLocalValue([value[0], value[1]]);
  }, [value[0]?.getTime(), value[1]?.getTime()]);

  const shortcutItems: PickersShortcutsItem<MuiDateRange<Date>>[] = shortcuts
    .filter((s) => Array.isArray(s.value))
    .map((s) => ({
      label: s.label,
      getValue: () => {
        const [start, end] = s.value as [Date, Date];
        return [start, end];
      },
    }));

  return (
    <DesktopDateRangePicker
      value={localValue}
      calendars={2}
      format="dd/MM/yyyy"
      // Keep the calendar where the user navigated: don't auto-jump to the end
      // date's month after the start is picked (e.g. picking a 2022 start with a
      // 2026 end would otherwise snap the view back to 2026).
      disableAutoMonthSwitching
      closeOnSelect={applyOnSelect}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      {...(maxDate ? { maxDate } : {})}
      dayOfWeekFormatter={dayOfWeekFormatter}
      onChange={(newValue) => setLocalValue(newValue)}
      onAccept={(newValue) => {
        if (!newValue[0] || !newValue[1]) return;
        // MUI commits (fires onAccept) on click-away too, which would re-apply
        // the filter and trigger a needless refetch / "Apply" flash. Only apply
        // when the range actually changed (compared by day, ignoring time) from
        // the committed value — so reverting to the same range does nothing.
        if (sameDay(newValue[0], value[0]) && sameDay(newValue[1], value[1])) return;
        onAccept([newValue[0], newValue[1]]);
      }}
      slots={{
        field: SingleInputDateRangeField,
        openPickerIcon: CalendarTodayIcon,
        // Clickable month/year header (+ year-jump arrows) so users can reach a
        // different year quickly instead of stepping month by month.
        calendarHeader: RangeCalendarHeader,
        // Bare selector (contextualization) has no toolbar/helper text.
        ...(applyOnSelect ? {} : { toolbar: RangeToolbar }),
      }}
      slotProps={{
        ...(shortcutItems.length
          ? { shortcuts: { items: shortcutItems, changeImportance: 'set' as const } }
          : {}),
        // No Update button when applying on selection.
        actionBar: { actions: applyOnSelect ? [] : ['accept'] },
        toolbar: applyOnSelect
          ? { hidden: true }
          : // Pass the live range to the custom toolbar so it shows the loaded value.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ({ hidden: false, rangeValue: localValue } as any),
        // Hyphen separator (MUI defaults to an en-dash) to match the previous design.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        field: { dateSeparator: '-' } as any,
        // Open the picker when clicking anywhere on the field, not just the icon.
        textField: {
          size: 'small',
          fullWidth: true,
          onClick: () => setOpen(true),
          sx: { ...dateRangeFieldSx, cursor: 'pointer', '& input': { cursor: 'pointer' } },
        },
        desktopPaper: { sx: dateRangeDesktopPaperSx },
      }}
      localeText={{ okButtonLabel: 'Update' }}
    />
  );
}
